import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
    page: number;
    totalPages: number;
    total: number;
    limit: number;
    onPageChange: (page: number) => void;
}

export default function Pagination({ page, totalPages, total, limit, onPageChange }: PaginationProps) {
    if (totalPages <= 1) {
        return (
            <div className="flex items-center justify-between px-5 py-3 border-t border-[#E6E9ED] bg-[#F5F7FA]">
                <span className="text-xs text-[#ADB5BD]">총 {total}건</span>
                <div />
            </div>
        );
    }

    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, total);

    // Build page numbers to display
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
        pages.push(1);
        if (page > 3) pages.push('...');
        const rangeStart = Math.max(2, page - 1);
        const rangeEnd = Math.min(totalPages - 1, page + 1);
        for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
        if (page < totalPages - 2) pages.push('...');
        pages.push(totalPages);
    }

    const btnBase =
        'w-8 h-8 rounded-lg text-xs flex items-center justify-center transition';

    return (
        <div className="flex items-center justify-between px-5 py-3 border-t border-[#E6E9ED] bg-[#F5F7FA]">
            <span className="text-xs text-[#ADB5BD]">
                총 {total}건 중 {start}-{end} 표시
            </span>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    className={`${btnBase} border border-[#E6E9ED] bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                    <ChevronLeft className="w-3.5 h-3.5" />
                </button>

                {pages.map((p, idx) =>
                    p === '...' ? (
                        <span key={`dots-${idx}`} className="text-xs text-[#ADB5BD] px-1">
                            ...
                        </span>
                    ) : (
                        <button
                            key={p}
                            onClick={() => onPageChange(p)}
                            className={`${btnBase} ${p === page
                                    ? 'bg-[#1ABB9C] text-white font-semibold'
                                    : 'border border-[#E6E9ED] bg-white hover:bg-gray-50'
                                }`}
                        >
                            {p}
                        </button>
                    ),
                )}

                <button
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages}
                    className={`${btnBase} border border-[#E6E9ED] bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                    <ChevronRight className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}
